<template>
  <div class="all-tenants">
    <div class="section-header">
      <div class="section-header__left">
        <div class="section-header__icon">
          <i class="el-icon-s-cooperation"></i>
        </div>
        <div>
          <h2 class="section-header__title">{{ $t('settings.globalTenants') }}</h2>
          <p class="section-header__subtitle">{{ filteredTenants.length }} {{ $t('settings.entries') }}</p>
        </div>
      </div>

      <div class="section-header__actions">
        <base-button
          type="primary"
          icon="el-icon-plus"
          @click="$router.push({path: '/app/tenants/create'})">
          {{ $t('settings.tenantCreate') }}
        </base-button>
        <base-button
          icon="el-icon-refresh"
          :loading="loading"
          @click="refreshTenants">
          {{ $t('settings.refresh') }}
        </base-button>
      </div>
    </div>

    <div class="search-container">
      <el-input
        class="table-search"
        v-model="searchTerm"
        :placeholder="$t('settings.tenant.search')"
        prefix-icon="el-icon-search"
        clearable>
      </el-input>
      <div class="search-hint">{{ $t('settings.searchHint') }}</div>
    </div>

    <div class="table-card">
      <base-table
        :data="filteredTenants"
        :loading="loading"
        :empty-text="$t('settings.tenantsTableEmpty')"
        highlight-current-row
        class="connections-table"
        style="width: 100%"
        @current-change="onRowSelected">

        <el-table-column prop="name" min-width="180" :label="$t('settings.name')">
          <template slot-scope="scope">
            <span class="cell-bold">{{ scope.row.name }}</span>
          </template>
        </el-table-column>

        <el-table-column width="140" align="center" :label="$t('settings.tenant.customerCount')">
          <template slot-scope="scope">
            <el-tag type="info" size="mini" class="domain-tag">
              <i class="el-icon-office-building"></i> {{ scope.row.customersCount || 0 }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column width="140" align="center" :label="$t('settings.tenant.accountCount')">
          <template slot-scope="scope">
            <el-tag type="success" size="mini" class="domain-tag">
              <i class="el-icon-user"></i> {{ scope.row.accountsCount || 0 }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column prop="createdDate" min-width="190" :label="$t('settings.createdAt')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.createdDate">
              <i class="el-icon-time cell-url__icon"></i>
              <span>{{ scope.row.createdDate | moment($t('settings.dateTimeFormat')) }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="updatedDate" min-width="190" :label="$t('settings.updatedAt')">
          <template slot-scope="scope">
            <div class="cell-url" v-if="scope.row.updatedDate">
              <i class="el-icon-refresh cell-url__icon"></i>
              <span>{{ scope.row.updatedDate | moment($t('settings.dateTimeFormat')) }}</span>
            </div>
            <span class="cell-empty" v-else>—</span>
          </template>
        </el-table-column>

        <el-table-column prop="active" width="110" align="center" :label="$t('settings.active')">
          <template slot-scope="scope">
            <el-switch
              v-model="scope.row.active"
              @click.native.stop
              @change="onSetActive(scope.row, scope.row.active)">
            </el-switch>
          </template>
        </el-table-column>

        <el-table-column align="center" fixed="right" width="180" :label="$t('settings.operations')">
          <template slot-scope="tenant">
            <div class="action-cell">
              <el-tooltip :content="$t('settings.edit')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-edit"
                  size="mini"
                  circle
                  @click.stop.prevent="onTenantEdit(tenant.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.showTenantAccounts')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-s-custom"
                  size="mini"
                  circle
                  @click.stop.prevent="goToTenantAccounts(tenant.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.showTenantCustomers')" placement="top">
                <el-button
                  type="primary"
                  icon="el-icon-office-building"
                  size="mini"
                  circle
                  @click.stop.prevent="goToTenantCustomers(tenant.row)">
                </el-button>
              </el-tooltip>
              <el-tooltip :content="$t('settings.deleteTenant')" placement="top">
                <el-button
                  type="danger"
                  icon="el-icon-delete"
                  size="mini"
                  circle
                  @click.stop.prevent="onDeleteTenant(tenant.row)">
                </el-button>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>
      </base-table>
    </div>

    <base-delete-dialog
      :visible.sync="deleteDialogVisible"
      :title="$t('settings.deleteTenantConfirm')"
      :text="$t('settings.deleteTenantWarning')"
      :confirm-label="$t('settings.deleteTenant')"
      :loading="loading"
      @confirm="confirmDeleteTenant">
    </base-delete-dialog>
  </div>
</template>

<script>
import BaseButton from '@/components/common/BaseButton';
import {createNamespacedHelpers} from 'vuex';
import BaseTable from '@/components/common/BaseTable';
import BaseDeleteDialog from '@/components/common/BaseDeleteDialog';

const tenantStateMappers = createNamespacedHelpers('tenant');

export default {
  name: "AllTenants",
  components: {BaseTable, BaseDeleteDialog, BaseButton},
  data() {
    return {
      loading: false,
      searchTerm: '',
      deleteDialogVisible: false,
      tenantToDelete: null
    }
  },
  computed: {
    ...tenantStateMappers.mapGetters({tenants: 'getTenants'}),
    filteredTenants() {
      if (!this.searchTerm) {
        return this.tenants;
      }

      const searchLower = this.searchTerm.toLowerCase();
      return this.tenants.filter(tenant => {
        return (
          (tenant.name && tenant.name.toLowerCase().includes(searchLower)) ||
          (tenant.uniqueCode && tenant.uniqueCode.toLowerCase().includes(searchLower))
        );
      });
    }
  },
  mounted() {
    this.refreshTenants();
  },
  methods: {
    ...tenantStateMappers.mapActions(['getTenants', 'deleteTenant', 'setTenantActive']),
    refreshTenants() {
      this.loading = true;
      this.getTenants().finally(() => {
        this.loading = false;
      });
    },
    onTenantEdit(tenant) {
      this.$router.push({path: `/app/tenants/${tenant.id}/edit`});
    },
    goToTenantAccounts(tenant) {
      this.$router.push({path: `/app/tenants/${tenant.id}/accounts`});
    },
    goToTenantCustomers(tenant) {
      this.$router.push({path: `/app/tenants/${tenant.id}/customers`});
    },
    onRowSelected(tenant) {
      if (tenant && tenant.id) {
        this.$router.push({path: `/app/tenants/${tenant.id}/edit`});
      }
    },
    onDeleteTenant(tenant) {
      this.tenantToDelete = tenant;
      this.deleteDialogVisible = true;
    },
    onSetActive(tenant, active) {
      this.loading = true;
      this.setTenantActive({
        tenantId: tenant.id,
        isActive: active
      }).then(() => {
        this.$message.success(
          active
            ? this.$t('settings.tenant.activated')
            : this.$t('settings.tenant.deactivated')
        );
      }).catch(() => {
        this.$message.error(this.$t('settings.genericHttpError'));
        // reset status on error
        tenant.active = !active;
      }).finally(() => {
        this.loading = false;
      });
    },
    confirmDeleteTenant() {
      if (!this.tenantToDelete) {
        return;
      }
      this.loading = true;
      this.deleteTenant(this.tenantToDelete.id).then(() => {
        this.$message.success(this.$t('settings.tenantDeleted'));
        this.deleteDialogVisible = false;
        this.tenantToDelete = null;
      }).catch(() => {
        this.$message.error(this.$t('settings.tenantDeleteFailed'));
      }).finally(() => {
        this.loading = false;
      });
    }
  }
}
</script>

<style lang="scss">
.all-tenants .connections-table .el-table__body-wrapper tr {
  cursor: pointer;

  &.current-row td {
    background: var(--bg-hover) !important;
  }
}
</style>

<style scoped lang="scss">
.all-tenants {
  padding: 0 15px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__icon {
    width: 42px;
    height: 42px;
    border-radius: 10px;
    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      color: var(--bg-surface);
      font-size: 18px;
    }
  }

  &__title {
    margin: 0 0 2px;
    font-size: 17px;
    font-weight: 600;
    color: var(--text-primary);
    line-height: 1.3;
  }

  &__subtitle {
    margin: 0;
    font-size: 12px;
    color: var(--text-secondary);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    align-items: center;
    gap: 10px;
  }
}

.search-container {
  margin-bottom: 16px;

  .table-search {
    max-width: 400px;
    margin-bottom: 5px;
  }

  .search-hint {
    font-size: 12px;
    color: var(--text-secondary);
    margin-left: 5px;
  }
}

.table-card {
  background: var(--bg-surface);
  border-radius: 12px;
  border: 1px solid var(--border);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

.cell-bold {
  font-weight: 600;
  font-size: 13.5px;
  color: var(--text-primary);
}

.cell-url {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;

  &__icon {
    color: var(--text-muted);
    font-size: 14px;
    flex-shrink: 0;
  }
}

.cell-empty {
  color: var(--text-muted);
}

.domain-tag {
  font-weight: 500;
  border-radius: 6px;
}

.action-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

@media (max-width: 767px) {
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }
}
</style>
